// FLAGS: personal-data
// Type-the-client's-email confirmation step in front of destructive client actions.
import {
  withSnackbar,
  WithSnackbarProps,
} from "@src/components/SnackBarComponent";
import React from "react";
import { Dialog, DialogContent, TextField, FormControl } from "@mui/material";
import {
  DialogHeader,
  DialogActionButtons,
} from "@src/components/DialogToolkit";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { ResponsePsychologistClientResponse } from "@src/api/v1/data-contracts";

interface ConfirmEmailDialogProps extends WithSnackbarProps {
  open: boolean;
  onCancelled: () => void;
  onConfirmed: () => void;
  client: ResponsePsychologistClientResponse;
  title: string;
  description: string;
  iconLigature: string;
  "data-testid"?: string;
}

const ConfirmEmailDialog: React.FC<ConfirmEmailDialogProps> = ({
  open,
  onCancelled,
  onConfirmed,
  client,
  title,
  description,
  iconLigature,
  "data-testid": dataTestId = "confirm-email-dialog",
}) => {
  const { t } = useTranslation();
  const formik = useFormik({
    initialValues: { email: "" },
    // FLAGS: personal-data
    // Compares the typed address against the client's own email. This is a typo guard in front of an
    // irreversible action, not authorisation - onConfirmed runs whatever the caller wired to it, and the
    // server policy is the real check.
    validate(values) {
      if (!values.email) {
        return { email: t("ConfirmEmailDialog.Email.Error.Required") };
      }
      if (values.email.toUpperCase() !== client.email!.toUpperCase()) {
        return { email: t("ConfirmEmailDialog.Email.Error.Match") };
      }
      return {};
    },

    onSubmit: () => {
      onConfirmed();
    },
  });

  if (!client) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onCancelled} data-testid={dataTestId}>
      <DialogHeader
        iconLigature={iconLigature}
        title={title}
        outlined
        onDelete={undefined}
        onFilter={undefined}
        textRightButton={undefined}
      />

      <DialogContent className="p-3 -ml-3 -mr-3 -mt-3">
        <form onSubmit={formik.handleSubmit}>
          <FormControl fullWidth>
            <p className="sub2 pb-1">{t("ConfirmEmailDialog.Email.Label")}</p>
            <TextField
              error={!formik.isValid}
              label={description}
              name="email"
              id="email"
              type="email"
              fullWidth
              variant="standard"
              value={formik.values.email}
              helperText={formik.errors.email}
              autoFocus
              InputLabelProps={{ shrink: true }}
              onChange={(e) => {
                formik.setValues({ email: e.target.value });
              }}
            />
          </FormControl>
        </form>
      </DialogContent>
      <DialogActionButtons
        onCancel={(e) => {
          formik.handleReset(e);
          onCancelled();
        }}
        onSubmit={formik.submitForm}
        onCancelText={t("Common.Cancel")}
        onSubmitText={t("Common.Confirm")}
        disabled={undefined}
      />
    </Dialog>
  );
};
export default withSnackbar(ConfirmEmailDialog);
